/** @jsxImportSource @emotion/react */
import React from "react";
import * as Styles from "./styles";
import { Col, Drawer, Grid, Layout, Row } from "antd";
import { SideBar, SideBarLogo, ProfileItem } from "..";

import { Constant } from "../../common/constant";
import { Images } from "../../common/images";
import { UserGroupType } from "../../services/Auth/dto/signedIn.dto";

interface LayoutDashboardProps {
  profileFirstName: string;
  profileLastName: string;
  profileImage?: string;
  logoImage?: string;
  role?: UserGroupType;
  basePath?: "admin" | "";
}

type LayoutDashboardPropsType = React.PropsWithChildren<LayoutDashboardProps>;

const LayoutDashboardComponent: React.FC<LayoutDashboardPropsType> = ({
  children,
  profileFirstName,
  profileLastName,
  profileImage,
  logoImage,
  role,
  basePath,
}: LayoutDashboardPropsType) => {
  const screens = Grid.useBreakpoint();
  const isMobile = screens.lg === false;

  const [inlineCollapsed, setInlineCollapsed] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const onCollapsedClickHandle = () => setInlineCollapsed((value) => !value);
  const onDrawerOpenHandle = () => setDrawerOpen(true);
  const onDrawerCloseHandle = () => setDrawerOpen(false);

  const roleName = (() => {
    switch (role) {
      case "SuperModerator":
        return Constant.role.superModerator;
      case "Moderator":
        return Constant.role.moderator;
      case "Sale":
        return Constant.role.sale;
      default:
        return "";
    }
  })();

  if (isMobile) {
    return (
      <Layout css={Styles.layoutStyle}>
        <Layout.Header css={Styles.headerStyle}>
          <Row
            justify="space-between"
            align="middle"
            css={Styles.headerRowStyle}
          >
            <Col>
              <div
                onClick={onDrawerOpenHandle}
                css={Styles.headerCollapseIconContainer}
              >
                <img alt="open menu" src={Images.EXPAND} />
              </div>
            </Col>
            <Col>
              <a href={Constant.route.homeBuilder.dashboard}>
                <img src={logoImage} css={Styles.headerLogoStyle} />
              </a>
            </Col>
          </Row>
        </Layout.Header>
        <Drawer
          placement="left"
          width={273}
          closable={false}
          visible={drawerOpen}
          onClose={onDrawerCloseHandle}
          bodyStyle={Styles.drawerBodyStyle}
          headerStyle={Styles.drawerHeaderStyle}
        >
          <ProfileItem
            firstName={profileFirstName}
            lastName={profileLastName}
            roleName={roleName}
            imageUrl={profileImage}
            inlineCollapsed={false}
          />
          <SideBar
            role={role}
            basePath={basePath}
            onNavigate={onDrawerCloseHandle}
          />
        </Drawer>
        <Layout.Content css={Styles.mobileContentStyle}>
          {children}
        </Layout.Content>
      </Layout>
    );
  }

  return (
    <Layout css={Styles.layoutStyle}>
      <Layout.Sider
        collapsible
        collapsed={inlineCollapsed}
        width="273"
        trigger={null}
        css={Styles.siderStyle}
        collapsedWidth={102}
      >
        <SideBarLogo
          url={Constant.route.homeBuilder.dashboard}
          imageUrl={logoImage}
          onCollapsedClick={onCollapsedClickHandle}
          inlineCollapsed={inlineCollapsed}
        />
        <ProfileItem
          firstName={profileFirstName}
          lastName={profileLastName}
          roleName={roleName}
          imageUrl={profileImage}
          inlineCollapsed={inlineCollapsed}
        />
        <SideBar role={role} basePath={basePath} />
      </Layout.Sider>
      <Layout.Content>{children}</Layout.Content>
    </Layout>
  );
};

export const LayoutDashboard = React.memo(LayoutDashboardComponent);
